import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";

export type FinanceTransactionKind = "sale" | "purchase" | "expense";
export type FinancePaymentMethod = "cash" | "card_transfer" | "bank_transfer";

export interface FinanceOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface FinanceOperationOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function options(value?: FinanceOperationOptions) {
  return {
    ...(value?.clock ? { clock: value.clock } : {}),
    ...(value?.operationIdSource ? { idSource: value.operationIdSource } : {}),
  };
}

function validContext(context: FinanceOperationContext): boolean {
  return Boolean(context.actorId && context.farmId && context.deviceId && context.performedAt);
}

function positiveAmount(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

export function buildFinanceCounterpartyUpsertOperation(
  context: FinanceOperationContext,
  input: {
    counterpartyId: string;
    expectedVersion: number;
    firstName?: string;
    lastName?: string;
    phone?: string;
    note?: string;
    isCustomer: boolean;
    isVendor: boolean;
    isActive: boolean;
  },
  operationOptions?: FinanceOperationOptions,
): OfflineOperation {
  const firstName = input.firstName?.trim();
  const lastName = input.lastName?.trim();
  const phone = input.phone?.trim();
  const note = input.note?.trim();

  if (
    !validContext(context) ||
    !input.counterpartyId ||
    (!firstName && !lastName) ||
    !Number.isInteger(input.expectedVersion) ||
    input.expectedVersion < 0
  ) {
    throw new Error("FINANCE_COUNTERPARTY_INPUT_INVALID");
  }

  if ((phone && phone.length > 40) || (note && note.length > 1000)) {
    throw new Error("FINANCE_COUNTERPARTY_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "finance.counterparty.upsert",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.counterpartyId,
      performedAt: context.performedAt,
      baseVersion: String(input.expectedVersion),
      payload: {
        counterpartyId: input.counterpartyId,
        expectedVersion: input.expectedVersion,
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
        ...(phone ? { phone } : {}),
        ...(note ? { note } : {}),
        isCustomer: input.isCustomer,
        isVendor: input.isVendor,
        isActive: input.isActive,
      },
    },
    options(operationOptions),
  );
}

export function buildFinanceTransactionRecordOperation(
  context: FinanceOperationContext,
  input: {
    transactionId: string;
    transactionKind: FinanceTransactionKind;
    categoryCode: string;
    description: string;
    amountToman: number;
    quantity?: number;
    unit?: string;
    tankId?: string;
    counterpartyId?: string;
    occurredOn: string;
    initialPayment?: {
      paymentId: string;
      amountToman: number;
      paymentMethod: FinancePaymentMethod;
      note?: string;
    };
  },
  operationOptions?: FinanceOperationOptions,
): OfflineOperation {
  const categoryCode = input.categoryCode.trim().toLowerCase();
  const description = input.description.trim();
  const unit = input.unit?.trim();
  const paymentNote = input.initialPayment?.note?.trim();
  const initialPaidAmountToman = input.initialPayment?.amountToman ?? 0;

  if (
    !validContext(context) ||
    !input.transactionId ||
    !["sale", "purchase", "expense"].includes(input.transactionKind) ||
    !categoryCode ||
    categoryCode.length > 80 ||
    !description ||
    description.length > 500 ||
    !positiveAmount(input.amountToman) ||
    !DATE_PATTERN.test(input.occurredOn) ||
    (input.quantity !== undefined && !positiveAmount(input.quantity)) ||
    (unit !== undefined && unit.length > 40)
  ) {
    throw new Error("FINANCE_TRANSACTION_INPUT_INVALID");
  }

  if (
    initialPaidAmountToman < 0 ||
    initialPaidAmountToman > input.amountToman ||
    (initialPaidAmountToman > 0 &&
      (!input.initialPayment?.paymentId ||
        !["cash", "card_transfer", "bank_transfer"].includes(
          input.initialPayment.paymentMethod,
        ))) ||
    (paymentNote && paymentNote.length > 1000)
  ) {
    throw new Error("FINANCE_INITIAL_PAYMENT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "finance.transaction.record",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.transactionId,
      performedAt: context.performedAt,
      payload: {
        transactionId: input.transactionId,
        ...(input.initialPayment?.paymentId
          ? { initialPaymentId: input.initialPayment.paymentId }
          : {}),
        transactionKind: input.transactionKind,
        categoryCode,
        description,
        amountToman: input.amountToman,
        ...(input.quantity === undefined ? {} : { quantity: input.quantity }),
        ...(unit ? { unit } : {}),
        ...(input.tankId ? { tankId: input.tankId } : {}),
        ...(input.counterpartyId ? { counterpartyId: input.counterpartyId } : {}),
        occurredOn: input.occurredOn,
        initialPaidAmountToman,
        ...(initialPaidAmountToman > 0 && input.initialPayment
          ? { initialPaymentMethod: input.initialPayment.paymentMethod }
          : {}),
        ...(paymentNote ? { paymentNote } : {}),
      },
    },
    options(operationOptions),
  );
}

export function buildFinancePaymentRecordOperation(
  context: FinanceOperationContext,
  input: {
    paymentId: string;
    transactionId: string;
    expectedVersion: number;
    amountToman: number;
    paymentMethod: FinancePaymentMethod;
    paidOn: string;
    note?: string;
  },
  operationOptions?: FinanceOperationOptions,
): OfflineOperation {
  const note = input.note?.trim();

  if (
    !validContext(context) ||
    !input.paymentId ||
    !input.transactionId ||
    !Number.isInteger(input.expectedVersion) ||
    input.expectedVersion < 1 ||
    !positiveAmount(input.amountToman) ||
    !["cash", "card_transfer", "bank_transfer"].includes(input.paymentMethod) ||
    !DATE_PATTERN.test(input.paidOn) ||
    (note && note.length > 1000)
  ) {
    throw new Error("FINANCE_PAYMENT_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "finance.payment.record",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.transactionId,
      performedAt: context.performedAt,
      baseVersion: String(input.expectedVersion),
      payload: {
        paymentId: input.paymentId,
        transactionId: input.transactionId,
        expectedVersion: input.expectedVersion,
        amountToman: input.amountToman,
        paymentMethod: input.paymentMethod,
        paidOn: input.paidOn,
        ...(note ? { note } : {}),
      },
    },
    options(operationOptions),
  );
}

export function buildFinanceTransactionVoidOperation(
  context: FinanceOperationContext,
  input: {
    transactionId: string;
    expectedVersion: number;
    reason: string;
  },
  operationOptions?: FinanceOperationOptions,
): OfflineOperation {
  const reason = input.reason.trim();

  if (
    !validContext(context) ||
    !input.transactionId ||
    !Number.isInteger(input.expectedVersion) ||
    input.expectedVersion < 1 ||
    !reason ||
    reason.length > 500
  ) {
    throw new Error("FINANCE_VOID_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "finance.transaction.void",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.transactionId,
      performedAt: context.performedAt,
      baseVersion: String(input.expectedVersion),
      payload: {
        transactionId: input.transactionId,
        expectedVersion: input.expectedVersion,
        reason,
      },
    },
    options(operationOptions),
  );
}
